import { LogOut, Settings, Shield, ShieldCheck, User } from "lucide-react";

import { Badge } from "@/components/_atoms/badge";
import { Button } from "@/components/_atoms/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/_atoms/dropdown-menu";
import { Link } from "@/components/_molecules/navigation-controls/link";
import { signout } from "@/domain/_shared/auth/services/auth.service";
import { useDomainTranslation } from "@/domain/_shared/i18n/use-domain-translation";
import { useIsGuest } from "@/domain/_shared/module-access/hooks/use-is-guest";
import { UserAvatar } from "@/domain/user/components/user-avatar";
import { CurrentUser } from "@/domain/user/models/current-user-model";
import { displayNameForUser } from "@/domain/user/models/display-name-for-user";
import { useCurrentWorkspaceContext } from "@/domain/workspace/hooks/useCurrentWorkspaceContext";
import { makeSettingsUrl } from "@/domain/workspace/lib/make-workspace-url";
import { Workspace } from "@/domain/workspace/models/workspace-model";

interface UserMenuDropdownProps {
  workspace: Workspace;
  user: CurrentUser;
}

export const UserMenuDropdown = ({ workspace, user }: UserMenuDropdownProps) => {
  const { t } = useDomainTranslation("common");
  const { currentWorkspace } = useCurrentWorkspaceContext();

  const isGuest = useIsGuest();
  const isAdmin = currentWorkspace.workspaceMembership.role === "ADMIN";
  const roleLabel = isGuest ? t("sidebar.roleGuest") : isAdmin ? t("sidebar.roleAdmin") : t("sidebar.roleMember");
  const RoleIcon = isAdmin ? ShieldCheck : User;

  function handleSignout() {
    signout();
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          className="flex h-9 flex-initial select-none flex-row items-center gap-2 px-2 text-xs hover:bg-foreground/3"
          variant="ghost"
          size="xs"
          aria-label={t("sidebar.userMenu")}
        >
          <UserAvatar />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-64">
        <div className="flex items-center gap-3 px-2 py-2">
          <UserAvatar user={user} size="md" />
          <div className="flex min-w-0 flex-col gap-1">
            <span className="truncate text-sm font-semibold text-foreground">{displayNameForUser(user)}</span>
            <span className="truncate text-xs font-light text-muted-foreground">{user.email}</span>
            <Badge className="mt-0.5 w-fit gap-1" variant="secondary">
              <RoleIcon className="size-3" />
              {roleLabel}
            </Badge>
          </div>
        </div>
        <DropdownMenuSeparator />
        {user.superUser && (
          <DropdownMenuItem className="cursor-pointer gap-2" asChild>
            <Link to="/admin">
              <Shield className="size-4" />
              {t("admin.panel")}
            </Link>
          </DropdownMenuItem>
        )}
        <DropdownMenuItem className="cursor-pointer gap-2" asChild>
          <Link to={makeSettingsUrl({ workspace, path: "account/profile" })}>
            <Settings className="size-4" />
            {t("sidebar.settings")}
          </Link>
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem className="cursor-pointer gap-2" onClick={handleSignout}>
          <LogOut className="size-4" />
          {t("sidebar.logOut")}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
